// src/utils/validation.ts

/**
 * Validates a password against the Cognito password policy.
 * Returns true if the password meets all criteria:
 * - Length between 8 and 128 characters (inclusive)
 * - Contains at least one uppercase letter
 * - Contains at least one lowercase letter
 * - Contains at least one digit
 */
export function isValidPassword(password: string): boolean {
  if (password.length < 8 || password.length > 128) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/\d/.test(password)) return false;
  return true;
}

/**
 * Basic email format validation.
 * Returns true if the email:
 * - Contains exactly one '@' character
 * - Has a non-empty local part (before @)
 * - Has a non-empty domain part (after @) that contains at least one '.'
 *
 * Not RFC-compliant — just a basic sanity check.
 */
export function isValidEmail(email: string): boolean {
  const atIndex = email.indexOf('@');
  // Must contain '@' and it can't be the first or last character
  if (atIndex < 1) return false;

  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1);

  // Local part must be non-empty (already ensured by atIndex < 1 check)
  if (localPart.length === 0) return false;

  // Domain part must be non-empty and contain at least one '.'
  if (domainPart.length === 0) return false;
  if (!domainPart.includes('.')) return false;

  // The '.' cannot be at the start or end of the domain
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false;

  return true;
}
